import Big from 'big.js';
import { appointmentHours, engagementAt, supplierPayRate } from './supplierPay';

const toNumberString = (value) => {
	if (value === null || value === undefined || value === '') return '';
	if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
	return String(value).replace(/,/g, '').trim();
};

const toBig = (value) => {
	const normalized = toNumberString(value);
	if (!normalized) return new Big(0);
	try {
		return new Big(normalized);
	} catch {
		return new Big(0);
	}
};

const hasNumericValue = (value) => {
	if (value === null || value === undefined || value === '') return false;
	const normalized = toNumberString(value);
	if (!normalized) return false;
	return Number.isFinite(Number(normalized));
};

const normalizeStatus = (value) => String(value || '').trim().toLowerCase();
const isRefundStatus = (value) => normalizeStatus(value) === 'refund';
const DEFAULT_INVOICE_VAT_RATE = new Big(0.2);

const sumBy = (entries, selector) =>
	(entries || []).reduce((sum, entry) => sum.plus(toBig(selector(entry))), new Big(0));

const toBigOrNull = (value) => (hasNumericValue(value) ? toBig(value) : null);

const resolvePurchaseGrossAmount = (item = {}) => {
	const gross = toBigOrNull(item?.gross);
	if (gross !== null) return gross;
	return toBig(item?.net).plus(toBig(item?.vat));
};

const resolvePurchaseGrossImpact = (item = {}) => {
	const gross = resolvePurchaseGrossAmount(item);
	return isRefundStatus(item?.documentStatus) ? gross.abs().times(-1) : gross;
};

const normalizeVatRate = (value) => {
	const rate = toBigOrNull(value);
	if (rate === null || rate.lt(0)) return null;
	if (rate.gt(1)) return rate.div(100);
	return rate;
};

const isSourcedInvoice = (item = {}) =>
	Boolean(item?.sourceId || item?.sourceDocumentId || item?.status || item?.invoiceStatus);

const resolveInvoiceRevenueAmount = (item = {}) => {
	const subtotal = toBigOrNull(
		item?.subtotalAmount ??
		item?.invoiceContent?.summary?.subtotal ??
		item?.summary?.subtotal,
	);
	if (subtotal !== null) return subtotal;

	const total = toBigOrNull(
		item?.totalAmount ??
		item?.invoiceContent?.summary?.total ??
		item?.summary?.total,
	);
	if (total === null) return new Big(0);

	const vat = toBigOrNull(
		item?.vatAmount ??
		item?.invoiceContent?.summary?.vat ??
		item?.summary?.vat,
	);
	if (vat !== null) return total.minus(vat);

	const explicitVatRate = normalizeVatRate(
		item?.vatRate ??
		item?.invoiceContent?.vatRate ??
		item?.summary?.vatRate,
	);
	const fallbackVatRate =
		isSourcedInvoice(item) || (subtotal === null && vat === null)
			? DEFAULT_INVOICE_VAT_RATE
			: null;
	const vatRate = explicitVatRate ?? fallbackVatRate;
	if (vatRate && vatRate.gt(0)) {
		return total.div(new Big(1).plus(vatRate));
	}

	return total;
};

const toNormalizedBuildingName = (value) => String(value || '').trim() || 'Unassigned';

const resolveInvoiceLineAmount = (line = {}) => {
	const explicitAmount = toBigOrNull(line?.amount ?? line?.directAmount);
	if (explicitAmount !== null) return explicitAmount;
	const hours = toBigOrNull(line?.hours);
	const rate = toBigOrNull(line?.rate);
	if (hours !== null && rate !== null) {
		return hours.times(rate);
	}
	return new Big(0);
};

const collectInvoiceLines = (invoice = {}) => {
	const ranges = Array.isArray(invoice?.invoiceContent?.dateRanges)
		? invoice.invoiceContent.dateRanges
		: [];
	const customDates = Array.isArray(invoice?.invoiceContent?.customDates)
		? invoice.invoiceContent.customDates
		: [];
	const lines = [];
	ranges.forEach((range) => {
		if (Array.isArray(range?.items)) {
			lines.push(...range.items);
		}
	});
	customDates.forEach((entry) => {
		if (Array.isArray(entry?.items)) {
			lines.push(...entry.items);
		}
	});
	return lines;
};

const buildInvoiceBuildingBreakdown = (invoice = {}) => {
	const grouped = new Map();
	const lines = collectInvoiceLines(invoice);

	lines.forEach((line) => {
		const amount = resolveInvoiceLineAmount(line);
		if (amount.eq(0)) return;

		const buildingId = line?.buildingId ?? null;
		const buildingName = toNormalizedBuildingName(line?.buildingName);
		const key =
			buildingId !== null && buildingId !== undefined && buildingId !== ''
				? `id:${String(buildingId)}`
				: `name:${buildingName.toLowerCase()}`;

		if (!grouped.has(key)) {
			grouped.set(key, {
				buildingId,
				buildingName,
				amount: new Big(0),
			});
		}
		grouped.get(key).amount = grouped.get(key).amount.plus(amount);
	});

	if (!grouped.size) return [];

	const revenue = resolveInvoiceRevenueAmount(invoice);
	const groupedTotal = Array.from(grouped.values()).reduce(
		(sum, entry) => sum.plus(entry.amount),
		new Big(0),
	);
	const scale = groupedTotal.eq(0) ? new Big(1) : revenue.div(groupedTotal);

	return Array.from(grouped.values())
		.map((entry) => ({
			buildingId: entry.buildingId ?? null,
			buildingName: entry.buildingName,
			amount: Number(entry.amount.times(scale).toFixed(2)),
		}))
		.filter((entry) => Math.abs(entry.amount) > 0.005);
};

const resolveInvoiceBuildingLabel = (item = {}, breakdown = []) => {
	if (breakdown.length === 1) {
		return breakdown[0].buildingName || 'Unassigned';
	}
	if (breakdown.length > 1) {
		return breakdown.map((entry) => entry.buildingName).join(', ');
	}
	return (
		item?.building?.name ||
		item?.clientSnapshot?.buildingName ||
		item?.clientSnapshot?.building ||
		''
	);
};

const buildSnapshotId = (entry, fallbackPrefix) => {
	if (entry?.snapshotId) return entry.snapshotId;
	if (entry?.documentId) return String(entry.documentId);
	if (entry?.id) return String(entry.id);
	return `${fallbackPrefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

export const buildAppointmentsSnapshot = (appointments = [], settlements = new Map()) =>
	appointments.map((item) => {
    const saved = settlements.get(String(item.documentId || item.id));
    const type = saved?.engagementType || engagementAt(item.supplier, item.date);
    return ({
		snapshotId: buildSnapshotId(item, 'appt'),
		sourceId: item?.id ?? null,
		sourceDocumentId: item?.documentId ?? null,
		date: saved?.date ?? item?.date ?? null,
		buildingId: item?.building?.id ?? null,
		buildingName: saved?.buildingName ?? item?.building?.name ?? '',
		supplierId: item?.supplier?.id ?? null,
		supplierName: saved?.supplierName ?? item?.supplier?.name ?? '',
		serviceId: item?.service?.id ?? null,
		serviceName: saved?.serviceName ?? item?.service?.name ?? '',
		hours: saved?.hours ?? appointmentHours(item) ?? '',
		supplierRate: saved?.rate ?? supplierPayRate(item.service, type) ?? '',
    engagementType: type,
    costSource: saved ? 'settlement' : 'estimate',
    settlementAmount: saved?.amount ?? null,
    settlementPayment: saved?.payment ?? null,
    snapshotVersion: 2,
	}); });

export const buildPurchasesSnapshot = (purchases = []) =>
	purchases.map((item) => {
		const grossValue =
			item?.gross ?? resolvePurchaseGrossAmount(item).toString() ?? '';
		return {
			snapshotId: buildSnapshotId(item, 'purchase'),
			sourceId: item?.id ?? null,
			sourceDocumentId: item?.documentId ?? null,
			date: item?.date ?? null,
			buildingId: item?.building?.id ?? null,
			buildingName: item?.building?.name ?? '',
			cosId: item?.cos?.id ?? null,
			cosName: item?.cos?.name ?? '',
			gross: grossValue,
			documentStatus: item?.documentStatus ?? '',
			notes: item?.notes ?? '',
		};
	});

export const buildInvoicesSnapshot = (invoices = []) =>
	invoices.map((item) => {
		const buildingBreakdown = buildInvoiceBuildingBreakdown(item);
		return {
			snapshotId: buildSnapshotId(item, 'invoice'),
			sourceId: item?.id ?? null,
			sourceDocumentId: item?.documentId ?? null,
			invoiceNumber: item?.invoiceNumber ?? '',
			invoiceDate: item?.invoiceDate ?? null,
			clientId: item?.client?.id ?? null,
			clientName:
				item?.client?.name ||
				item?.clientSnapshot?.name ||
				item?.clientSnapshot?.company ||
				'',
			buildingId: item?.building?.id ?? null,
			buildingName: resolveInvoiceBuildingLabel(item, buildingBreakdown),
			buildingBreakdown,
			subtotalAmount: item?.invoiceContent?.summary?.subtotal ?? '',
			vatAmount: item?.invoiceContent?.summary?.vat ?? '',
			vatRate: item?.invoiceContent?.vatRate ?? null,
			totalAmount:
				item?.totalAmount ??
				item?.invoiceContent?.summary?.total ??
				item?.invoiceContent?.summary?.subtotal ??
				'',
			status: item?.invoiceStatus ?? 'unpaid',
		};
	});

export const buildSummaryFromSnapshots = ({
	appointments = [],
	purchases = [],
	invoices = [],
	wagePayments = [],
} = {}) => {
	const totalHours = sumBy(appointments, (item) => item?.hours || 0);
	const purchasesGross = (purchases || []).reduce(
		(sum, item) => sum.plus(resolvePurchaseGrossImpact(item)),
		new Big(0),
	);
	const revenue = (invoices || []).reduce(
		(sum, item) => sum.plus(resolveInvoiceRevenueAmount(item)),
		new Big(0),
	);
	const wagePaid = sumBy(wagePayments, (item) => item?.amount || 0);

	let laborCost = new Big(0);
	let missingSupplierRateHours = new Big(0);

	appointments.forEach((appointment) => {
		const hours = toBig(appointment?.hours);
		const supplierRate = appointment?.supplierRate;
		if (!hasNumericValue(supplierRate)) {
			if (hours.gt(0)) {
				missingSupplierRateHours = missingSupplierRateHours.plus(hours);
			}
			return;
		}
		laborCost = laborCost.plus(appointment.costSource === 'settlement' && hasNumericValue(appointment.settlementAmount)
      ? toBig(appointment.settlementAmount) : hours.times(toBig(supplierRate)));
	});

	const grossMargin = revenue.minus(laborCost).minus(purchasesGross);
	const marginPct = revenue.eq(0) ? null : grossMargin.div(revenue);
	const unassignedPurchases = sumBy(
		purchases.filter((purchase) => !purchase?.buildingId && !purchase?.buildingName),
		(item) => resolvePurchaseGrossImpact(item),
	);

	return {
		revenue: Number(revenue.toFixed(2)),
		laborCost: Number(laborCost.toFixed(2)),
		purchasesGross: Number(purchasesGross.toFixed(2)),
		grossMargin: Number(grossMargin.toFixed(2)),
		marginPct: marginPct === null ? null : Number(marginPct.times(100).toFixed(2)),
		totalHours: Number(totalHours.toFixed(2)),
		unassignedPurchases: Number(unassignedPurchases.toFixed(2)),
		missingSupplierRateHours: Number(missingSupplierRateHours.toFixed(2)),
		wagePaymentsTotal: Number(wagePaid.toFixed(2)),
		invoicePaymentDifference: Number(sumBy(wagePayments.filter((item) => item.kind === 'invoice' && item.appointmentsAmount !== null && item.appointmentsAmount !== undefined), (item) => toBig(item.amount).minus(toBig(item.appointmentsAmount))).toFixed(2)),
		appointmentCount: appointments.length,
		purchaseCount: purchases.length,
		invoiceCount: invoices.length,
		wagePaymentCount: wagePayments.length,
	};
};

export const calculateAppointmentPay = (appointment) => {
  if (appointment.costSource === 'settlement' && hasNumericValue(appointment.settlementAmount)) return Number(appointment.settlementAmount);
	const hours = toBig(appointment?.hours || 0);
	const rate = toBig(appointment?.supplierRate || 0);
	return Number(hours.times(rate).toFixed(2));
};

export const calculatePurchaseGrossImpact = (purchase) =>
	Number(resolvePurchaseGrossImpact(purchase).toFixed(2));

export const toSafeNumber = (value) => {
	if (value === null || value === undefined || value === '') return null;
	const normalized = Number(toNumberString(value));
	return Number.isFinite(normalized) ? normalized : null;
};
